import { useCallback } from 'react';

import { useQueryClient } from 'react-query';
import { Navigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CreateEventButton } from './components/create-event-button';
import { ExtractionProgressBanner } from './components/extraction-progress-banner';
import { ProceduralExtractionLoader } from './components/procedural-extraction-loader';
import { ProceduralTimelineEmptyState } from './components/procedural-timeline-empty-state';
import { ProceduralTimelineList } from './components/procedural-timeline-list';
import { ProceduralTimelineSkeleton } from './components/procedural-timeline-skeleton';
import { ProceduralUploadDialog } from './components/procedural-upload-dialog';
import { UploadTimetableButton } from './components/upload-timetable-button';
import { useProceduralDropzone } from './components/use-procedural-dropzone';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { ProceduralStreamBlock } from '../../../../../../../backend/src/matter/procedural-timeline/dto/procedural-stream-block.response.dto';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import { OrgPermission } from '../../../../../../../backend/src/organisation/organisation-permission';
import { useProceduralStream, type ExtractionState } from '@/api/hooks/useProceduralStream';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetProceduralTimelineEvents } from '@/api/queries/useGetProceduralTimelineEvents';
import { useGetProceduralTimelineSuggestions } from '@/api/queries/useGetProceduralTimelineSuggestions';
import { useGetProceduralTimelineUploadEligibility } from '@/api/queries/useGetProceduralTimelineUploadEligibility';
import { QueryEntity } from '@/api/types';
import { useUserContext } from '@/Contexts/User';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { usePermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { TimelineCalculator } from '@/new/components/timeline-calculator';

export function ProceduralTimeline() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const { isEnabled } = useOrgPermissions();
  const { data: matter } = useGetMatterV2(matterId);
  const matterBreadcrumbHead = useMatterBreadcrumbHead(matterId, matter?.name, false);
  const { allowed: canEdit } = usePermission(Permission.ProceduralTimelineEdit);
  const { data: events, isLoading: isLoadingEvents } = useGetProceduralTimelineEvents(matterId);
  // Suggestions are proposed edits; only fetch them for users who can act on
  // them. View-only users would otherwise hit a 403 on the suggestions route.
  const { data: suggestions, isLoading: isLoadingSuggestions } = useGetProceduralTimelineSuggestions(matterId, {
    enabled: canEdit,
  });
  // Upload eligibility only matters for editors (it drives the upload/dropzone
  // UI) and the route is edit-gated, so skip it for view-only users.
  const { data: eligibility } = useGetProceduralTimelineUploadEligibility(matterId, canEdit);

  const activeStreamFileId =
    eligibility && !eligibility.canUpload && eligibility.blockedBy === 'extraction_in_flight'
      ? eligibility.inFlightFileId
      : null;
  const isExtractionInFlight = activeStreamFileId !== null;

  const handleTerminal = useCallback(
    async (block: ProceduralStreamBlock, finalState: ExtractionState) => {
      const suggestionsKey = [QueryEntity.ProceduralTimelineSuggestions, { matterId }];
      queryClient.invalidateQueries([QueryEntity.ProceduralTimelineUploadEligibility, { matterId }]);
      queryClient.invalidateQueries([QueryEntity.ProceduralTimelineEvents, { matterId }]);
      const suggestionsRefetch = queryClient.invalidateQueries(suggestionsKey);

      if (block.type === 'completed') {
        await suggestionsRefetch;
        const pending = queryClient.getQueryData<ProceduralTimelineEventSuggestion[]>(suggestionsKey) ?? [];
        if (pending.length > 0) {
          toast.success('Extraction complete. Review suggestions below.');
        } else if ((finalState.persisted?.skipped ?? 0) > 0) {
          toast.info(
            'Extraction complete. No new events or updates were suggested: everything in the document matched the existing timeline.',
          );
        } else {
          toast.warning('Extraction finished, but no procedural events were found in this document.');
        }
      } else if (block.type === 'failed' || block.type === 'rejected') {
        toast.error(`Extraction failed: ${block.error}`);
      }
    },
    [queryClient, matterId],
  );

  const { state: extractionState } = useProceduralStream({
    matterId,
    fileId: activeStreamFileId,
    onTerminal: handleTerminal,
  });

  // Drop a timetable anywhere on the page to start an extraction. Gated on edit
  // permission; eligibility (in-flight / pending) is handled inside the hook.
  const dropzone = useProceduralDropzone({ matterId, enabled: canEdit });

  // Wait for user data before deciding to redirect — otherwise the permission
  // check evaluates against an empty set and we bounce orgs that *do* have the
  // flag while the user payload is still in flight.
  if (!user) return null;
  if (!isEnabled(OrgPermission.ProceduralTimelines)) {
    return <Navigate to={`/matters/${matterId}`} replace />;
  }

  // Suggestions are only fetched for editors, so view-only users never resolve
  // the suggestions query — don't block load on it for them.
  const suggestionsLoaded = !canEdit || (!isLoadingSuggestions && suggestions !== undefined);
  const hasLoaded = !isLoadingEvents && events !== undefined && suggestionsLoaded;
  // Suggestions are proposed edits; view-only users can't act on them.
  const visibleSuggestions = canEdit ? (suggestions ?? []) : [];
  const hasContent = hasLoaded && (events.length > 0 || visibleSuggestions.length > 0);
  // Treat a failed extraction as "not in flight" — the toast surfaces the
  // error; the page should fall through to the empty state, not a fail card.
  const showExtractionLoader = hasLoaded && isExtractionInFlight && extractionState.phase !== 'failed' && !hasContent;
  const isEmpty = hasLoaded && !hasContent && (!isExtractionInFlight || extractionState.phase === 'failed');

  return (
    <>
      <Breadcrumb
        items={[
          ...matterBreadcrumbHead,
          {
            href: `/matters/${matterId}/procedural-timeline`,
            label: 'Procedural Timeline',
            active: true,
          },
        ]}
      />

      <PageContainer className="flex min-h-0 flex-1 flex-col pb-4">
        <PageHeader className="mb-5 shrink-0">
          <PageTitle text="Procedural Timeline" subtext="Store and track the procedural events for this matter." />

          <div className="flex items-center gap-2">
            <CreateEventButton matterId={matterId} events={events ?? []} />
            <UploadTimetableButton matterId={matterId} />
          </div>
        </PageHeader>

        {isExtractionInFlight && hasContent && <ExtractionProgressBanner state={extractionState} />}

        <div {...dropzone.getRootProps({ className: 'relative flex min-h-0 flex-1 flex-col' })}>
          <input {...dropzone.getInputProps()} />

          {canEdit && dropzone.isDragActive && (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-blue-500 bg-blue-50/80 backdrop-blur-[1px]">
              <div className="flex flex-col items-center gap-2 text-blue-600">
                <Icon className="lucide--upload-cloud text-2xl" />
                <p className="font-medium">
                  {dropzone.upload.isDisabled
                    ? (dropzone.upload.blockedReason ?? 'Upload unavailable right now')
                    : 'Drop your timetable to upload'}
                </p>
              </div>
            </div>
          )}

          {!hasLoaded ? (
            <ProceduralTimelineSkeleton />
          ) : showExtractionLoader ? (
            <ProceduralExtractionLoader state={extractionState} />
          ) : isEmpty ? (
            <ProceduralTimelineEmptyState onBrowse={canEdit ? dropzone.open : undefined} />
          ) : (
            <ProceduralTimelineList
              events={events}
              suggestions={visibleSuggestions}
              isExtractionInFlight={isExtractionInFlight}
            />
          )}
        </div>

        <ProceduralUploadDialog upload={dropzone.upload} />
      </PageContainer>

      <TimelineCalculator />
    </>
  );
}
